import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { LOGO_URL, scrollToId, scrollToTop } from "./shared";

const links = [
  { label: "Home", id: "home" },
  { label: "About", id: "about" },
  { label: "Services", id: "services" },
  { label: "Why Us", id: "why-us" },
  { label: "Contact", id: "contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (id === "home") {
      goHome();
      return;
    }
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 120);
    } else {
      scrollToId(id);
    }
  };

  const goHome = () => {
    setOpen(false);
    if (location.pathname !== "/") navigate("/");
    else scrollToTop();
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      data-testid="navbar"
      className={`fixed top-0 z-50 w-full transition-all duration-300 ${
        scrolled ? "glass py-3" : "py-5"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6">
        <button
          onClick={goHome}
          data-testid="nav-logo"
          className="flex items-center gap-3"
        >
          <img
            src={LOGO_URL}
            alt="Gabicom"
            className="h-12 w-auto object-contain"
          />
        </button>

        <div className="hidden items-center gap-9 md:flex">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              data-testid={`nav-link-${l.id}`}
              className="font-mono-tech text-sm uppercase tracking-widest text-[#a0aec0] transition-colors hover:text-white"
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => go("contact")}
            data-testid="nav-cta"
            className="rounded-full bg-gradient-to-r from-[#00f0ff] to-[#b026ff] px-5 py-2.5 text-sm font-semibold text-[#05050a] transition-transform hover:scale-105"
          >
            Get in touch
          </button>
        </div>

        <button
          className="text-white md:hidden"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X /> : <Menu />}
        </button>
      </nav>

      {open && (
        <div className="glass mt-3 flex flex-col gap-2 px-6 py-4 md:hidden" data-testid="mobile-menu">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              data-testid={`mobile-link-${l.id}`}
              className="py-2 text-left font-mono-tech text-sm uppercase tracking-widest text-[#a0aec0]"
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
    </motion.header>
  );
}
